/**
 * PhysicsWorld - Rapier.js Physics Engine Wrapper
 * Handles 3D physics simulation (replaces jBullet from Java version)
 */

import * as THREE from 'three';

/**
 * Rigid-frame delta between a body's base pose and its current pose.
 *
 * Returns the transform D such that for any point p expressed in the scene
 * frame at base time:  p_now = D.quaternion * p + D.position
 *
 *   D.quaternion = currentQuaternion * baseQuaternion^-1
 *   D.position   = currentPosition - D.quaternion * basePosition
 *
 * This matches syncMeshes() exactly: a mesh at basePosition + offset (with
 * base rotation) ends up at currentPosition + currentQuaternion * offset,
 * so rotation happens about the body's centre, not the origin.
 *
 * @param {THREE.Vector3} basePosition - Body translation at base time
 * @param {THREE.Quaternion} baseQuaternion - Body rotation at base time
 * @param {THREE.Vector3} currentPosition - Body translation now
 * @param {THREE.Quaternion} currentQuaternion - Body rotation now
 * @returns {{position: THREE.Vector3, quaternion: THREE.Quaternion}}
 */
export function computeFrameDelta(basePosition, baseQuaternion, currentPosition, currentQuaternion) {
    const quaternion = currentQuaternion.clone().multiply(baseQuaternion.clone().invert());
    const position = currentPosition.clone().sub(basePosition.clone().applyQuaternion(quaternion));
    return { position, quaternion };
}

export class PhysicsWorld {
    constructor() {
        this.RAPIER = null;
        this.world = null;
        this.bodies = new Map(); // Map Three.js objects to Rapier bodies
        this.compoundBodies = []; // Array of {body, meshes, offsets} for compound bodies
        this.gravity = { x: 0, y: -9.81, z: 0 };
    }

    /**
     * Initialize Rapier physics engine (async WASM load)
     */
    async init() {
        console.log('Initializing Rapier.js physics...');

        try {
            // Dynamic import for -compat version (iOS/Capacitor compatible)
            this.RAPIER = await import('@dimforge/rapier3d-compat');
            await this.RAPIER.init();

            // Create physics world with gravity
            this.world = new this.RAPIER.World(this.gravity);

            // Create ground plane at y=0
            this.createGroundPlane();

            console.log('Rapier.js physics initialized with ground plane');

            // Update UI status
            const status = document.getElementById('physics-status');
            if (status) status.innerHTML = '<span class="status">Ready</span>';

            return true;
        } catch (error) {
            console.error('Failed to initialize physics:', error);

            const status = document.getElementById('physics-status');
            if (status) status.innerHTML = '<span class="error">Failed</span>';

            throw error;
        }
    }

    /**
     * Create a static ground plane at y=-5
     */
    createGroundPlane() {
        // Nominal walking-surface height, read by spawn/placement code
        // (the actual slab top sits at groundY + 0.05)
        this.groundY = -5;
        // Create ground plane body
        // Thick slab with its TOP at y=-4.95 (same walking surface as the
        // old thin plate): heavy stacks (figure sitting on the chair)
        // pushed bodies through a 0.05-half-height plate via solver
        // penetration
        const groundBodyDesc = this.RAPIER.RigidBodyDesc.fixed()
            .setTranslation(0, -5.45, 0);

        const groundBody = this.world.createRigidBody(groundBodyDesc);

        const groundColliderDesc = this.RAPIER.ColliderDesc.cuboid(50, 0.5, 50)
            .setRestitution(0.2)
            .setFriction(0.9);

        this.world.createCollider(groundColliderDesc, groundBody);

        console.log('Ground plane created at y=-5');
    }

    /**
     * Create a dynamic rigid body (moves with physics)
     * @param {THREE.Object3D} mesh - Three.js mesh to sync with
     * @param {Object} options - Body options
     * @returns {Object} Rapier rigid body
     */
    createDynamicBody(mesh, options = {}) {
        const {
            mass = 1.0,
            restitution = 0.3,
            friction = 0.5,
            linearDamping = 0.1,
            angularDamping = 0.1
        } = options;

        // Create rigid body description
        const bodyDesc = this.RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(mesh.position.x, mesh.position.y, mesh.position.z)
            .setLinearDamping(linearDamping)
            .setAngularDamping(angularDamping);

        const body = this.world.createRigidBody(bodyDesc);

        // Create collider (box shape based on mesh)
        const size = this.getObjectSize(mesh);
        const colliderDesc = this.RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
            .setMass(mass)
            .setRestitution(restitution)
            .setFriction(friction);

        this.world.createCollider(colliderDesc, body);

        // Store reference
        this.bodies.set(mesh, body);

        return body;
    }

    /**
     * Create a static rigid body (doesn't move, e.g., ground)
     * @param {THREE.Object3D} mesh - Three.js mesh to sync with
     * @param {Object} options - Body options
     * @returns {Object} Rapier rigid body
     */
    createStaticBody(mesh, options = {}) {
        const {
            restitution = 0.3,
            friction = 0.5
        } = options;

        // Create static body at mesh position
        const bodyDesc = this.RAPIER.RigidBodyDesc.fixed()
            .setTranslation(mesh.position.x, mesh.position.y, mesh.position.z);

        const body = this.world.createRigidBody(bodyDesc);

        // Extract vertices and indices from mesh geometry
        if (mesh.geometry && mesh.geometry.attributes.position) {
            const vertices = mesh.geometry.attributes.position.array;
            const indices = mesh.geometry.index ? mesh.geometry.index.array : null;

            try {
                let colliderDesc;

                if (indices) {
                    // Create trimesh collider from geometry
                    colliderDesc = this.RAPIER.ColliderDesc.trimesh(vertices, indices)
                        .setRestitution(restitution)
                        .setFriction(friction);
                } else {
                    // Fallback to convex hull if no indices
                    colliderDesc = this.RAPIER.ColliderDesc.convexHull(vertices)
                        .setRestitution(restitution)
                        .setFriction(friction);
                }

                if (colliderDesc) {
                    this.world.createCollider(colliderDesc, body);
                    console.log('Created trimesh/convex collider for static body');
                }
            } catch (error) {
                console.warn('Failed to create mesh collider, falling back to cuboid:', error);
                // Fallback to cuboid if mesh collider fails
                const size = this.getObjectSize(mesh);
                const colliderDesc = this.RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
                    .setRestitution(restitution)
                    .setFriction(friction);
                this.world.createCollider(colliderDesc, body);
            }
        } else {
            // No geometry, use cuboid
            const size = this.getObjectSize(mesh);
            const colliderDesc = this.RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
                .setRestitution(restitution)
                .setFriction(friction);
            this.world.createCollider(colliderDesc, body);
        }

        this.bodies.set(mesh, body);

        return body;
    }

    /**
     * Create dynamic (movable) body from Three.js mesh
     * @param {THREE.Object3D} mesh - Three.js mesh to sync with
     * @param {Object} options - Body options
     * @returns {Object} Rapier rigid body
     */
    createDynamicBody(mesh, options = {}) {
        const {
            restitution = 0.3,
            friction = 0.5,
            mass = 1.0
        } = options;

        // Create dynamic body at mesh position and rotation
        const bodyDesc = this.RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(mesh.position.x, mesh.position.y, mesh.position.z)
            .setRotation({
                x: mesh.quaternion.x,
                y: mesh.quaternion.y,
                z: mesh.quaternion.z,
                w: mesh.quaternion.w
            });

        const body = this.world.createRigidBody(bodyDesc);

        // Extract vertices and indices from mesh geometry
        if (mesh.geometry && mesh.geometry.attributes.position) {
            const vertices = mesh.geometry.attributes.position.array;
            const indices = mesh.geometry.index ? mesh.geometry.index.array : null;

            try {
                let colliderDesc;

                if (indices) {
                    // Create trimesh collider from geometry
                    colliderDesc = this.RAPIER.ColliderDesc.trimesh(vertices, indices)
                        .setRestitution(restitution)
                        .setFriction(friction)
                        .setDensity(mass);
                } else {
                    // Fallback to convex hull if no indices
                    colliderDesc = this.RAPIER.ColliderDesc.convexHull(vertices)
                        .setRestitution(restitution)
                        .setFriction(friction)
                        .setDensity(mass);
                }

                if (colliderDesc) {
                    this.world.createCollider(colliderDesc, body);
                    console.log('Created trimesh/convex collider for dynamic body');
                }
            } catch (error) {
                console.warn('Failed to create mesh collider, falling back to cuboid:', error);
                // Fallback to cuboid if mesh collider fails
                const size = this.getObjectSize(mesh);
                const colliderDesc = this.RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
                    .setRestitution(restitution)
                    .setFriction(friction)
                    .setDensity(mass);
                this.world.createCollider(colliderDesc, body);
            }
        } else {
            // No geometry, use cuboid
            const size = this.getObjectSize(mesh);
            const colliderDesc = this.RAPIER.ColliderDesc.cuboid(size.x / 2, size.y / 2, size.z / 2)
                .setRestitution(restitution)
                .setFriction(friction)
                .setDensity(mass);
            this.world.createCollider(colliderDesc, body);
        }

        this.bodies.set(mesh, body);

        return body;
    }

    /**
     * Create a compound dynamic body from multiple meshes (for connected chair pieces)
     * More efficient than individual bodies - uses simplified convex hulls
     * @param {Array<THREE.Mesh>} meshes - Array of meshes to combine
     * @param {Object} options - Body options
     * @returns {Object} Rapier rigid body
     */
    createCompoundDynamicBody(meshes, options = {}) {
        const {
            restitution = 0.3,
            friction = 0.8,
            mass = 1.0
        } = options;

        if (!meshes || meshes.length === 0) {
            console.warn('No meshes provided for compound body');
            return null;
        }

        // Calculate center of mass of all meshes
        const centerOfMass = new THREE.Vector3();
        meshes.forEach(mesh => {
            centerOfMass.add(mesh.position);
        });
        centerOfMass.divideScalar(meshes.length);

        console.log(`Creating compound body from ${meshes.length} meshes at center (${centerOfMass.x.toFixed(2)}, ${centerOfMass.y.toFixed(2)}, ${centerOfMass.z.toFixed(2)})`);
        console.log(`Current compound bodies count before creation: ${this.compoundBodies.length}`);

        // Create dynamic body at center of mass
        const bodyDesc = this.RAPIER.RigidBodyDesc.dynamic()
            .setTranslation(centerOfMass.x, centerOfMass.y, centerOfMass.z);

        console.log('About to create rigid body...');
        const body = this.world.createRigidBody(bodyDesc);
        console.log('Successfully created rigid body');

        // Add simplified convex hull colliders for each mesh (relative to center of mass)
        let colliderCount = 0;
        let totalVertices = 0;

        for (const mesh of meshes) {
            if (!mesh.geometry || !mesh.geometry.attributes.position) {
                console.warn('Mesh has no geometry, skipping');
                continue;
            }

            const vertices = mesh.geometry.attributes.position.array;
            totalVertices += vertices.length / 3;

            // Transform vertices into the physics frame, then relative to
            // the body center
            const transformedVertices = new Float32Array(vertices.length);
            const tempVertex = new THREE.Vector3();

            // LOCAL matrix only, never matrixWorld: the chair meshes live
            // inside the SceneRotationPivot group, and the physics world
            // (ground plane, gravity, ragdoll, this body's translation
            // computed from mesh.position above) all operate in the
            // pivot's LOCAL frame - the view rotation is purely visual.
            // Using matrixWorld baked the current view rotation into the
            // hulls while the body translation stayed pivot-local, so the
            // collision body was rotated away from the meshes whenever the
            // view wasn't at the default front rotation (same convention
            // as the STL exporter: local matrix, pivot must not leak in).
            mesh.updateMatrix();

            for (let i = 0; i < vertices.length; i += 3) {
                tempVertex.set(vertices[i], vertices[i + 1], vertices[i + 2]);

                // Apply the mesh's own transform (position/rotation/scale)
                tempVertex.applyMatrix4(mesh.matrix);

                // Make relative to body center
                tempVertex.sub(centerOfMass);

                transformedVertices[i] = tempVertex.x;
                transformedVertices[i + 1] = tempVertex.y;
                transformedVertices[i + 2] = tempVertex.z;
            }

            try {
                // TRIMESH, not convex hull: the side panels of most chairs
                // are deeply CONCAVE (a lounger's seat curve), and a convex
                // hull fills the whole hollow with invisible solid - the
                // figure could never sink into the seat, it sat on the
                // hull's "lid". A trimesh follows the real surface exactly
                // (and matches what the Shift+H collider view draws).
                // Panels are closed extrusions, so Rapier can still derive
                // mass properties from them.
                let colliderDesc = null;
                const geomIndex = mesh.geometry.index;
                let indices;
                if (geomIndex) {
                    indices = geomIndex.array instanceof Uint32Array
                        ? geomIndex.array : Uint32Array.from(geomIndex.array);
                } else {
                    // Non-indexed geometry: consecutive vertex triples
                    indices = new Uint32Array(transformedVertices.length / 3);
                    for (let i = 0; i < indices.length; i++) indices[i] = i;
                }
                colliderDesc = this.RAPIER.ColliderDesc.trimesh(transformedVertices, indices);
                if (!colliderDesc) {
                    // Degenerate mesh: fall back to the old convex hull
                    colliderDesc = this.RAPIER.ColliderDesc.convexHull(transformedVertices);
                }

                if (colliderDesc) {
                    colliderDesc
                        .setRestitution(restitution)
                        .setFriction(friction)
                        .setDensity(mass / meshes.length); // Distribute mass across pieces

                    this.world.createCollider(colliderDesc, body);
                    colliderCount++;
                }
            } catch (error) {
                console.warn(`Failed to create convex hull for mesh, skipping:`, error);
            }
        }

        // Give the chair a REALISTIC mass. The auto-computed mass (unit
        // density x thin-panel volumes) lands around ~1kg-equivalent, so
        // the ~60kg figure blasted the chair away on contact. Target a
        // plywood-chair ~8kg for a believable figure:chair ratio (~7:1).
        const CHAIR_TARGET_MASS = 8;
        const autoMass = body.mass();
        if (autoMass < CHAIR_TARGET_MASS) {
            body.setAdditionalMass(CHAIR_TARGET_MASS - autoMass, true);
        }
        console.log(`Compound body mass: ${body.mass().toFixed(1)} (auto ${autoMass.toFixed(2)} + ballast)`);

        console.log(`Compound body created: ${colliderCount} trimesh colliders from ${totalVertices} total vertices`);

        // Store compound body with all meshes and their offsets from center of mass
        const meshOffsets = meshes.map(mesh => ({
            mesh: mesh,
            offset: mesh.position.clone().sub(centerOfMass),
            rotation: mesh.quaternion.clone()
        }));

        this.compoundBodies.push({
            body: body,
            centerOfMass: centerOfMass.clone(),
            meshData: meshOffsets
        });

        console.log(`Stored compound body with ${meshOffsets.length} meshes for synchronized updates`);

        return body;
    }

    /**
     * Get approximate size of Three.js object for collider
     */
    getObjectSize(mesh) {
        if (mesh.geometry) {
            mesh.geometry.computeBoundingBox();
            const box = mesh.geometry.boundingBox;
            return {
                x: box.max.x - box.min.x,
                y: box.max.y - box.min.y,
                z: box.max.z - box.min.z
            };
        }
        return { x: 1, y: 1, z: 1 };
    }

    /**
     * Step physics simulation forward
     * @param {number} deltaTime - Time step in seconds
     */
    step(deltaTime = 1 / 60) {
        if (!this.world) return;

        this.world.step();
    }

    /**
     * Sync Three.js mesh positions with physics bodies
     */
    syncMeshes() {
        // Sync individual bodies
        this.bodies.forEach((body, mesh) => {
            if (!body) {
                this.bodies.delete(mesh);
                return;
            }

            try {
                const translation = body.translation();
                mesh.position.set(translation.x, translation.y, translation.z);

                const rotation = body.rotation();
                mesh.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
            } catch (error) {
                // Body is invalid (was removed from world), remove from map
                this.bodies.delete(mesh);
            }
        });

        // Sync compound bodies (all meshes move together)
        for (const compoundData of this.compoundBodies) {
            const { body, meshData } = compoundData;

            if (!body) continue;

            try {
                // Get physics body's current position and rotation
                const bodyPos = body.translation();
                const bodyRot = body.rotation();

                const bodyPosition = new THREE.Vector3(bodyPos.x, bodyPos.y, bodyPos.z);
                const bodyQuaternion = new THREE.Quaternion(bodyRot.x, bodyRot.y, bodyRot.z, bodyRot.w);

                // Update each mesh with its offset from the physics body
                for (const meshInfo of meshData) {
                    const { mesh, offset, rotation } = meshInfo;

                    // Apply body rotation to the offset
                    const rotatedOffset = offset.clone().applyQuaternion(bodyQuaternion);

                    // Set mesh position = body position + rotated offset
                    mesh.position.copy(bodyPosition).add(rotatedOffset);

                    // Set mesh rotation = body rotation * original rotation
                    mesh.quaternion.copy(bodyQuaternion).multiply(rotation);
                }
            } catch (error) {
                console.warn('Failed to sync compound body:', error);
            }
        }
    }

    /**
     * Delta transform of the chair compound body: current pose relative to
     * its pose at body creation. The body is created at the meshes' centre
     * of mass with identity rotation (createCompoundDynamicBody), which is
     * the pose the static sketch overlay / base planes correspond to, so
     * applying this delta to the overlay makes it track the meshes EXACTLY
     * (same math as syncMeshes).
     *
     * @returns {{position: THREE.Vector3, quaternion: THREE.Quaternion}|null}
     *          Delta, or null when there is no (valid) compound body
     */
    getCompoundBodyDelta() {
        const compound = this.compoundBodies[0];
        if (!compound || !compound.body) return null;

        try {
            const t = compound.body.translation();
            const r = compound.body.rotation();
            return computeFrameDelta(
                compound.centerOfMass,          // creation translation
                new THREE.Quaternion(),         // creation rotation (identity)
                new THREE.Vector3(t.x, t.y, t.z),
                new THREE.Quaternion(r.x, r.y, r.z, r.w)
            );
        } catch (error) {
            // Body was removed from the world
            return null;
        }
    }

    /**
     * Apply impulse to a body
     */
    applyImpulse(mesh, impulse) {
        const body = this.bodies.get(mesh);
        if (body) {
            body.applyImpulse(impulse, true);
        }
    }

    /**
     * Apply force to a body
     */
    applyForce(mesh, force) {
        const body = this.bodies.get(mesh);
        if (body) {
            body.addForce(force, true);
        }
    }

    /**
     * Remove body from physics world
     */
    removeBody(mesh) {
        const body = this.bodies.get(mesh);
        if (body) {
            this.world.removeRigidBody(body);
            this.bodies.delete(mesh);
        }
    }

    /**
     * Remove all compound bodies from physics world
     */
    clearCompoundBodies() {
        console.log(`Clearing ${this.compoundBodies.length} compound bodies...`);

        while (this.compoundBodies.length > 0) {
            const compoundData = this.compoundBodies.pop();
            if (compoundData && compoundData.body) {
                try {
                    console.log('Removing compound body from physics world');
                    this.world.removeRigidBody(compoundData.body);
                    console.log('Successfully removed compound body');
                } catch (error) {
                    console.error('Failed to remove compound body:', error);
                }
            }
        }

        console.log(`Compound bodies array length after clear: ${this.compoundBodies.length}`);
    }

    /**
     * Get physics body for mesh
     */
    getBody(mesh) {
        return this.bodies.get(mesh);
    }

    /**
     * Reset a body's position, rotation, and velocities
     * @param {THREE.Object3D} mesh - The mesh whose body to reset
     * @param {THREE.Vector3} position - Position to reset to
     * @param {THREE.Quaternion} quaternion - Rotation to reset to
     */
    resetBodyTransform(mesh, position, quaternion) {
        const body = this.bodies.get(mesh);
        if (body) {
            // Set position
            body.setTranslation({ x: position.x, y: position.y, z: position.z }, true);

            // Set rotation
            body.setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w }, true);

            // Clear velocities
            body.setLinvel({ x: 0, y: 0, z: 0 }, true);
            body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        }
    }

    /**
     * Reset a compound body's position, rotation, and velocities
     * @param {THREE.Vector3} position - Position to reset to
     * @param {THREE.Quaternion} quaternion - Rotation to reset to
     */
    resetCompoundBodyTransform(position, quaternion) {
        for (const compoundData of this.compoundBodies) {
            const { body } = compoundData;
            if (body) {
                // Set position
                body.setTranslation({ x: position.x, y: position.y, z: position.z }, true);

                // Set rotation
                body.setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w }, true);

                // Clear velocities
                body.setLinvel({ x: 0, y: 0, z: 0 }, true);
                body.setAngvel({ x: 0, y: 0, z: 0 }, true);
            }
        }
    }

    /**
     * Send every compound body back to the pose it was created at (its centre
     * of mass, identity rotation) - the pose the static sketch overlay and the
     * base planes correspond to. Used when there is no pre-play snapshot to
     * restore, e.g. the chair was only hand-dragged while physics was paused.
     */
    resetCompoundBodiesToCreationPose() {
        for (const compoundData of this.compoundBodies) {
            const { body, centerOfMass } = compoundData;
            if (!body) continue;
            body.setTranslation(
                { x: centerOfMass.x, y: centerOfMass.y, z: centerOfMass.z }, true);
            body.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
            body.setLinvel({ x: 0, y: 0, z: 0 }, true);
            body.setAngvel({ x: 0, y: 0, z: 0 }, true);
        }
    }

    /**
     * Cleanup physics world
     */
    dispose() {
        if (this.world) {
            this.bodies.clear();
            this.world.free();
            this.world = null;
        }
    }
}
